// Copyright ©️ 2026 eVolve MEP, LLC

import { useMemo } from 'react';

import type { SyncTimesResponse } from '@evolvemep/api-ts';
import { AgGridReact } from 'ag-grid-react';

import { AgGridContainer } from 'helpers/ag-grid/AgGridContainer';
import { baseColumnDef, compactRowHeight, baseGridDef } from 'helpers/ag-grid/baseColumnDef';
import { strongEntries } from 'helpers/strongEntries';
import type { AnalyticsChartProps } from 'modules/Shop/Insights/RevitUsage/types';
import {
  getBaseSyncColDefs,
  documentEmailColDef,
  type SyncTimesTree,
} from 'modules/Shop/Insights/RevitUsage/views/charts/SyncTimes/columnDefs';
import { useAnalyticsChart } from 'modules/Shop/Insights/RevitUsage/views/charts/useAnalyticsChart';

const aggregateData = (data: SyncTimesResponse[]): SyncTimesTree[] => {
  const recordsByAttachment = data.reduce(
    (out, row) => {
      const { document } = row;
      if (document in out) {
        (out[document] as SyncTimesResponse[]).push(row);
      } else {
        out[document] = [row];
      }
      return out;
    },
    {} as Record<SyncTimesResponse['document'], SyncTimesResponse[]>,
  );
  return strongEntries(recordsByAttachment).map(([document, records]) => ({
    document,
    distinctSessionCount: records.reduce((out, curr) => out + curr.distinctSessionCount, 0),
    distinctUserCount: records.reduce((out, curr) => out + curr.distinctUserCount, 0),
    totalExecutions: records.reduce((out, curr) => out + curr.totalExecutions, 0),
    averageExecutionTime: records.reduce((out, curr) => out + curr.averageExecutionTime, 0) / records.length,
    averageRecordsPerActiveDay:
      records.reduce((out, curr) => out + curr.averageRecordsPerActiveDay, 0) / records.length,
    totalExecutionFailures: records.reduce((out, curr) => out + curr.totalExecutionFailures, 0),
    children: records,
  }));
};

export const DocumentSyncTimesChart = ({ showDemo, usedFilters }: AnalyticsChartProps<SyncTimesResponse[]>) => {
  const [data, loading] = useAnalyticsChart('usage-stats/v1/reporting/syncTimes/users', { usedFilters }, aggregateData);

  const colDefs = useMemo(() => getBaseSyncColDefs({ isCompact: showDemo ?? false }), [showDemo]);
  const defaultColDef = useMemo(
    () => ({
      ...baseColumnDef,
      suppressHeaderContextMenu: true,
      contextMenuItems: [],
    }),
    [],
  );
  return (
    <AgGridContainer mt="xs" pb="xs">
      {(applyWrap) => (
        <AgGridReact
          {...baseGridDef}
          loading={loading}
          rowData={data}
          treeData
          treeDataChildrenField="children"
          columnDefs={colDefs}
          autoGroupColumnDef={documentEmailColDef}
          rowHeight={compactRowHeight}
          headerHeight={compactRowHeight}
          defaultColDef={defaultColDef}
          onFirstDataRendered={applyWrap}
        />
      )}
    </AgGridContainer>
  );
};
